'use client'

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/platform/v2/ui/button'
import { CaseStudy } from '@/data/cases'
import CaseLinksDialog from './CaseLinksDialog'
import { cn } from '@/lib/utils'

interface Props {
    item: CaseStudy
    size?: 'flagship' | 'medium' | 'small'
    className?: string
}

const CaseCard = ({ item, size = 'medium', className }: Props) => {
    const [open, setOpen] = useState(false)
    const flagship = size === 'flagship'
    const tags = item.tasks.slice(0, flagship ? 6 : 4)

    return (
        <>
            <article className={cn('surface surface-hover group flex h-full flex-col gap-5', flagship && 'lg:p-8', className)}>
                <div className="flex flex-1 flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <span
                            className={cn(
                                'flex items-center justify-center rounded-xl bg-primary font-extrabold text-primary-foreground',
                                flagship ? 'h-11 w-11 text-sm' : 'h-9 w-9 text-xs',
                            )}
                            aria-hidden
                        >
                            {item.marker}
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-bold uppercase tracking-[0.14em] text-foreground">{item.client}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.category}</p>
                        </div>
                        <span className="pill pill-soon ml-auto">Тариф {item.plan}</span>
                    </div>

                    <h3 className={cn('font-extrabold tracking-[-0.02em] text-foreground', flagship ? 'display-3' : 'text-lg')}>
                        {item.shortDescription}
                    </h3>

                    {size !== 'small' && <p className="body-text">{item.heroSubtitle}</p>}

                    <ul className="flex flex-wrap gap-2">
                        {tags.map((t) => (
                            <li
                                key={t}
                                className="rounded-full border border-border/60 bg-[hsl(var(--surface))] px-3 py-1 text-[11px] font-semibold text-foreground/80"
                            >
                                {t}
                            </li>
                        ))}
                    </ul>

                    <Button
                        type="button"
                        variant="link"
                        onClick={() => setOpen(true)}
                        className="btn-link mt-auto h-auto w-fit p-0 no-underline hover:no-underline"
                        aria-label={`Смотреть кейс: ${item.client}`}
                    >
                        Смотреть кейс <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                </div>
            </article>
            <CaseLinksDialog item={item} open={open} onOpenChange={setOpen} />
        </>
    )
}

export default CaseCard
